import {
	CalendarOutlined,
	DollarOutlined,
	ExclamationCircleOutlined,
	GiftOutlined,
	SearchOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import { Button, Modal, Select, Switch, Table, Tag, Tooltip } from 'antd';
import Buttons from 'components/Global/Buttons';
import InputTimeLogsHoursField from 'components/InputTimeLogsHoursField';
import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
	formatDate,
	invalidText,
	minutesToTimeString,
	timeStringToMinutes,
	toastText,
} from 'utils/utils';
import styles from './index.module.scss';
import { useNavigate } from 'react-router-dom';
import Ellipse from 'components/Global/Ellipse';
import SearchComponent from 'components/Global/SearchComponent';
import { timeLogsAssignmentApi } from 'Api/timeLogsAssignment';
import dayjs from 'dayjs';

const { Column } = Table;

const UploadPreview = () => {
	const { data } = useSelector((state: any) => state.ocrTimeLogs);

	const { data: profileData } = useSelector(
		(state: any) => state?.userProfile
	);
	const navigate = useNavigate();
	const [showConfirmModal, setShowConfirmModal] = useState(false);

	const [searchValue, setSearchValue] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [shiftOptions, setShiftOptions] = useState<any[]>([]);
	const [isSunday, setIsSunday] = useState(false);
	const [isHoliday, setIsHoliday] = useState('');
	const [timeLogsData, setTimeLogsData] = useState<any[]>([]);

	const [pendingToggleState, setPendingToggleState] = useState(false);
	const [isSundayRegularHours, setIsSundayRegularHours] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [_supervisorId, setSupervisorId] = useState(null);
	const [date, setDate] = useState<any>(null);

	const calculateTotals = (employees: any[]) => {
		let totalRegularHours = 0;
		let totalTimeShiftHours = 0;
		let totalOvertimeHours = 0;
		let totalHours = 0;

		employees.forEach((item: any) => {
			if (!item.isTotal) {
				totalRegularHours += timeStringToMinutes(
					item.regularHours || '00:00'
				);
				totalOvertimeHours += timeStringToMinutes(
					item.overtimeHours || '00:00'
				);
				totalHours += timeStringToMinutes(item.totalHours || '00:00');

				// Only add to timeShiftHours if this employee has NIGHT shift
				if (
					item.shiftDetails?.shiftType === 'NIGHT' &&
					!item.leaveDetails?.isPaidLeave
				) {
					totalTimeShiftHours += timeStringToMinutes(
						item.timeShiftHours || '00:00'
					);
				}
			}
		});

		return {
			isTotal: true,
			employeeName: 'Total',
			regularHours: minutesToTimeString(totalRegularHours),
			timeShiftHours: minutesToTimeString(totalTimeShiftHours),
			overtimeHours: minutesToTimeString(totalOvertimeHours),
			totalHours: minutesToTimeString(totalHours),
			invalidRegularHours: false,
			invalidTimeShiftHours: false,
			invalidOvertimeHours: false,
		};
	};

	const filteredTimeLogsData = useMemo(() => {
		if (!searchValue.trim()) {
			return timeLogsData;
		}

		const searchLower = searchValue.toLowerCase().trim();

		// Filter employees (excluding total row)
		const filteredEmployees = timeLogsData
			.filter((item) => !item.isTotal)
			.filter((item) => {
				const nameMatch = item.employeeName
					?.toLowerCase()
					.includes(searchLower);
				const codeMatch = item.employeeCode
					?.toLowerCase()
					.includes(searchLower);
				return nameMatch || codeMatch;
			});

		// Recalculate totals based on filtered data
		if (filteredEmployees.length > 0) {
			const totalsRow = calculateTotals(filteredEmployees);
			return [...filteredEmployees, totalsRow];
		}

		return filteredEmployees;
	}, [timeLogsData, searchValue]);

	const confirmToggle = () => {
		setIsSundayRegularHours(pendingToggleState);

		// const updatedEmployees = timeLogsData
		// 	.filter((item) => !item.isTotal)
		// 	.map((item: any) => {
		// 		const regularHours = item.isForeman ? 720 : 480;
		// 		const updatedItem = {
		// 			...item,
		// 			regularHours: pendingToggleState
		// 				? minutesToTimeString(regularHours)
		// 				: '00:00',
		// 			overtimeHours: pendingToggleState
		// 				? '00:00'
		// 				: minutesToTimeString(0),
		// 		};

		// 		const shiftMins =
		// 			timeStringToMinutes(updatedItem.regularHours) +
		// 			timeStringToMinutes(updatedItem.overtimeHours);

		// 		updatedItem.timeShiftHours = minutesToTimeString(shiftMins);
		// 		updatedItem.totalHours = minutesToTimeString(shiftMins);

		// 		return updatedItem;
		// 	});

		// // Recalculate totals
		// const totalsRow = calculateTotals(updatedEmployees);
		// const finalData = [...updatedEmployees, totalsRow];

		// // Update both current and original data
		// setTimeLogsData(finalData);

		setShowConfirmModal(false);
	};

	const handleSearch = (value: string) => {
		setSearchValue(value);
	};

	const handleLogChange = (id: string, name: string, value: any) => {
		const shouldTreatAsOvertime = isSunday && !isSundayRegularHours;

		// Find the current employee data
		const currentEmployee = timeLogsData.find(
			(item) => item.employeeId === id
		);
		if (!currentEmployee) return;

		// Calculate the updated fields based on the change
		let updatedFields = {};

		if (name === 'totalHours') {
			const totalMinutes = timeStringToMinutes(value);
			let calculatedRegular = '00:00';
			let calculatedOvertime = '00:00';
			const REGULAR_HOURS = currentEmployee.isForeman ? 720 : 480;

			if (shouldTreatAsOvertime) {
				calculatedOvertime = value;
			} else {
				const regularMinutes = Math.min(totalMinutes, REGULAR_HOURS);
				const overtimeMinutes = Math.max(
					0,
					totalMinutes - REGULAR_HOURS
				);
				calculatedRegular = minutesToTimeString(regularMinutes);
				calculatedOvertime = minutesToTimeString(overtimeMinutes);
			}

			updatedFields = {
				totalHours: value,
				regularHours: calculatedRegular,
				overtimeHours: calculatedOvertime,
				timeShiftHours: value,
				invalidRegularHours: false,
				invalidTimeShiftHours: false,
				invalidOvertimeHours: false,
				hasManualTotalHours: true, // This is a MANUAL change
			};
		} else if (name === 'shiftDetails') {
			updatedFields = {
				shiftDetails: value,
				timeShiftHours:
					value.shiftType === 'NIGHT' &&
					currentEmployee.leaveDetails?.isPaidLeave
						? '00:00'
						: minutesToTimeString(
								timeStringToMinutes(
									currentEmployee.regularHours
								) +
									timeStringToMinutes(
										currentEmployee.overtimeHours
									)
						  ),
				invalidRegularHours: false,
				invalidTimeShiftHours: false,
				invalidOvertimeHours: false,
			};
		} else {
			updatedFields = {
				[name]: value,
				invalidRegularHours: false,
				invalidTimeShiftHours: false,
				invalidOvertimeHours: false,
			};
		}

		// Update the UI data
		const updatedData = timeLogsData.map((item) => {
			if (item.employeeId === id) {
				return { ...item, ...updatedFields };
			} else if (item.isTotal) {
				return item;
			} else {
				return {
					...item,
					invalidRegularHours: false,
					invalidTimeShiftHours: false,
					invalidOvertimeHours: false,
				};
			}
		});

		// Recalculate totals
		const employees = updatedData.filter((item) => !item.isTotal);
		const totalsRow = calculateTotals(employees);
		const finalData = [...employees, totalsRow];

		setTimeLogsData(finalData);
	};

	const handleSaveClick = () => {
		setIsModalOpen(true);
	};

	const handleCancel = () => {
		setIsModalOpen(false);
	};

	const cancelToggle = () => {
		setShowConfirmModal(false);
	};

	const myButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'secondary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: false,
			onclick: () => {
				cancelToggle();
			},
		},
		{
			isLoading: false,
			text: 'Confirm',
			className: 'btn-blue',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: true,
			onclick: () => {
				confirmToggle();
			},
		},
	];

	useEffect(() => {
		if (invalidText(data)) {
			navigate('/daily-attendance');
			return;
		}
		const res = data;
		const responseData = res?.data?.data || {};
		setTimeLogsData(responseData.data);
		setIsSunday(res.data.data?.isSunday);
		setIsHoliday(res.data.data?.holidayName);
		if (responseData.shiftOptions) {
			const formattedShiftOptions = responseData.shiftOptions.map(
				(shift: any) => ({
					label: `${shift.shiftName} (${shift.shiftType})`,
					value: shift.shiftType,
					shiftId: shift.id,
					...shift,
				})
			);
			setShiftOptions(formattedShiftOptions);
		}
		if (res.data.date) {
			setDate(dayjs(res.data.date));
		}
		setSupervisorId(res?.data?.supervisorId);
	}, []);

	const handleToggleChange = (checked: any) => {
		setPendingToggleState(checked);
		setShowConfirmModal(true);
	};

	const handleSubmit = async () => {
		try {
			setIsSubmitting(true);
			const employeeHoursData = timeLogsData
				.filter(
					(item) =>
						!item.isTotal ||
						item.isPayrollRun ||
						(item.originalSupervisorInfo &&
							!item.originalSupervisorInfo.canEdit)
				)
				.map((item) => {
					const regularMinutes = timeStringToMinutes(
						item.regularHours || '00:00'
					);
					const overtimeMinutes = timeStringToMinutes(
						item.overtimeHours || '00:00'
					);

					const attendanceStatus =
						!invalidText(isHoliday) && overtimeMinutes === 0
							? 'ABSENT'
							: regularMinutes === 0 && overtimeMinutes === 0
							? 'ABSENT'
							: 'PRESENT';

					return {
						employeeId: item.employeeId,
						regularHours: regularMinutes,
						overtimeHours: overtimeMinutes,
						timeShiftHours: timeStringToMinutes(
							item.timeShiftHours || '00:00'
						),
						attendanceStatus: attendanceStatus,
						shiftId:
							item.shiftDetails?.shiftId || item.shiftDetails?.id,
					};
				});

			const submitData = {
				date: dayjs(date).format('YYYY-MM-DD'),
				employeeHoursData,
				isRegularHours: isSundayRegularHours,
				supervisorId: _supervisorId,
			};

			const response = await timeLogsAssignmentApi.createHourlyLogs(
				submitData
			);

			if (response?.data) {
				toastText('Time logs submitted successfully', 'success');
			}
			setIsModalOpen(false);
			navigate('/daily-attendance');
		} catch (error: any) {
			const message =
				error?.response?.data?.message || 'Failed to submit time logs';
			toastText(message, 'error');
		} finally {
			setIsSubmitting(false);
		}
	};
	return (
		<>
			<div className={styles['modal-header']}>
				<div className={styles['time-logs-preview-header']}>
					<SearchComponent
						className={styles['time-logs-preview-header-item']}
						placeHolder="Search by code or name"
						prefixIcon={<SearchOutlined />}
						handleChange={handleSearch}
						handleChangeApi={() => {}}
						value={searchValue}
						size="large"
					/>
				</div>

				<div className={styles['modal-header-filter']}>
					{isSunday && (
						<Tooltip title="All hours on Sunday are considered overtime">
							<Tag
								style={{
									cursor: 'pointer',
									fontSize: '14px',
									padding: '8px',
								}}
								color="#584495"
							>
								<div
									style={{
										display: 'flex',
										gap: '7px',
									}}
								>
									<span>
										<CalendarOutlined />
									</span>
									<span>Sunday</span>
								</div>
							</Tag>
						</Tooltip>
					)}
					{!invalidText(isHoliday) && (
						<Tooltip title="All hours on Holiday are considered overtime">
							<Tag
								style={{
									cursor: 'pointer',
									fontSize: '14px',
									padding: '8px',
								}}
								color="#584495"
							>
								<div
									style={{
										display: 'flex',
										gap: '7px',
									}}
								>
									<span>
										<GiftOutlined />
									</span>
									<span>{isHoliday}</span>
								</div>
							</Tag>
						</Tooltip>
					)}
					{isSunday && !isHoliday && (
						<Tooltip title="Sunday calculation details">
							<div className={styles['header-toggle']}>
								<Switch
									checked={isSundayRegularHours}
									onChange={handleToggleChange}
									disabled={false}
								/>
								<p
									style={{
										fontSize: '14px',
									}}
								>
									Sunday as Regular Hours
								</p>
							</div>
						</Tooltip>
					)}
				</div>
			</div>
			<Table
				dataSource={filteredTimeLogsData}
				pagination={false}
				scroll={{ y: 'calc(100vh - 267px)' }}
				rowKey={(record) => record.id}
				loading={false}
				rowClassName={(record, index) =>
					index === timeLogsData.length - 1 ? 'sticky-row' : ''
				}
			>
				<Column
					title="Employee Name | Code"
					dataIndex="employeeName"
					key="employeeName"
					width="35%"
					className="bg-white"
					render={(text: string, record: any) => {
						const timeForEmployee = record.isForeman
							? 720
							: record.requiredTotalHours;
						if (record.isTotal)
							return (
								<span className="color-purple font-bold">
									Total {filteredTimeLogsData.length - 1}{' '}
									{filteredTimeLogsData.length - 1 > 1
										? 'Employees'
										: 'Employee'}
								</span>
							);
						let title = '';
						if (record.leaveDetails.isPaidLeave)
							title = 'On Full Day Paid leave';
						else if (record.leaveDetails.isLwp)
							title = 'On Full Day LWP leave';

						return (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									whiteSpace: 'nowrap',
								}}
							>
								<Tooltip title={title}>
									<span
										style={{
											color:
												record.leaveDetails.isLwp ||
												record.leaveDetails
													.isPaidLeave ||
												'inherit',
											display: 'flex',
											alignItems: 'center',
											gap: '0.5rem',
										}}
									>
										{record.isPayrollRun &&
											record?.payrollStatus && (
												<Tooltip
													title={
														record.payrollStatus ===
															'APPROVED' ||
														record.payrollStatus ===
															'FINALIZE'
															? 'Payroll is Approved/Finalized'
															: record.payrollStatus ===
															  'DRAFT'
															? 'Payroll is in Draft'
															: 'Payroll is Pending'
													}
												>
													<DollarOutlined
														style={{
															color:
																record.payrollStatus ===
																	'APPROVED' ||
																record.payrollStatus ===
																	'FINALIZE'
																	? 'green'
																	: record.payrollStatus ===
																	  'DRAFT'
																	? 'orange'
																	: 'grey',
														}}
													/>
												</Tooltip>
											)}
										{record.employeeName} |{' '}
										<span style={{ color: '#584495' }}>
											{record.employeeCode}
										</span>
									</span>
								</Tooltip>
								{timeStringToMinutes(record.totalHours) <
									timeForEmployee &&
									!isSunday &&
									invalidText(isHoliday) && (
										<>
											<Tooltip title="Employee Worked hours are below the standard hours. Please verify">
												<WarningOutlined
													style={{
														color: 'orange',
														cursor: 'pointer',
													}}
												/>
											</Tooltip>
										</>
									)}
							</div>
						);
					}}
				/>

				<Column
					title="Shift Type"
					dataIndex="shiftType"
					key="shiftType"
					width="25%"
					className="bg-white"
					render={(value: any, record: any) => {
						if (record.isTotal)
							return <span className="font-bold">-</span>;

						const currentShiftType =
							record.shiftDetails?.shiftType || value;

						return (
							<Select
								value={currentShiftType}
								disabled={record.isPayrollRun}
								onChange={(newValue) => {
									const selectedShift = shiftOptions.find(
										(opt: any) => opt.value === newValue
									);
									const updatedShiftDetails = {
										shiftType: newValue,
										shiftName:
											selectedShift?.shiftName ||
											newValue,
										shiftId: selectedShift?.id,
									};
									const newData = [...timeLogsData];
									const index = newData.findIndex(
										(item) =>
											item.employeeId ===
											record.employeeId
									);
									if (index > -1) {
										newData[index] = {
											...newData[index],
											shiftDetails: updatedShiftDetails,
										};
										setTimeLogsData(newData);
										handleLogChange(
											record.employeeId,
											'shiftDetails',
											updatedShiftDetails
										);
									}
								}}
								options={shiftOptions}
								style={{
									fontSize: '14px',
									width: '195px',
								}}
								size="middle"
								popupMatchSelectWidth={false}
								placeholder="Select shift"
							/>
						);
					}}
				/>

				<Column
					title="Worked Hours"
					dataIndex="totalHours"
					key="totalHours"
					width="25%"
					className="bg-white"
					render={(value: any, record: any) => {
						if (record.isTotal) {
							return (
								<p
									className="color-purple font-bold"
									style={{
										textAlign: 'right',
										width: '50%',
									}}
								>
									{record.totalHours}
								</p>
							);
						}
						return (
							<InputTimeLogsHoursField
								value={record.totalHours}
								onChange={(newValue) =>
									handleLogChange(
										record.employeeId,
										'totalHours',
										newValue
									)
								}
								label=""
								required={false}
								name="totalHours"
								isError={false}
								disabled={
									record.leaveDetails.isLwp ||
									record.leaveDetails.isPaidLeave ||
									record.isPayrollRun
								}
								showLabel={false}
								size="middle"
								style={{ width: '50%', textAlign: 'right' }}
								is8hoursRequired={
									!invalidText(isHoliday) && !isSunday
								}
								isForeman={record?.isForeman}
							/>
						);
					}}
				/>

				<Column
					title="Shift Difference Hours"
					dataIndex="timeShiftHours"
					key="timeShiftHours"
					width="20%"
					className="bg-white"
					render={(value: any, record: any) => {
						if (record.isTotal) {
							return (
								<span className="color-purple font-bold">
									{record.timeShiftHours}
								</span>
							);
						}
						console.log(record, 'record.overTimeHours');
						const currentShiftType = record.shiftDetails?.shiftType;
						const displayValue =
							isHoliday && currentShiftType === 'NIGHT'
								? record.overtimeHours
								: currentShiftType === 'NIGHT'
								? record.timeShiftHours
								: '-';

						return (
							<span className="color-purple">{displayValue}</span>
						);
					}}
				/>

				<Column
					title="Reason"
					dataIndex="reason"
					key="reason"
					width="25%"
					className="bg-white"
					render={(value: any, record: any) => {
						if (invalidText(record.reason) || record.isTotal) {
							return <span className="font-bold">-</span>;
						}

						return (
							<Ellipse
								maxLength={30}
								message={record?.reason}
								isTooltip={true}
								tooltipMessage={record?.reason}
								key={record?.employeeId}
							/>
						);
					}}
				/>

				<Column
					title="Original Supervisor"
					dataIndex="originalSupervisor"
					key="originalSupervisor"
					width="25%"
					className="bg-white"
					render={(value: any, record: any) => {
						if (
							record.isTotal ||
							invalidText(record.originalSupervisorInfo)
						) {
							return <p>-</p>;
						}
						const { supervisorName, modifiedBy, modifiedAt } =
							record.originalSupervisorInfo;
						return (
							<div>
								<p>Original Supervisor : {supervisorName}</p>
								{modifiedBy && (
									<p>Modified By : {modifiedBy}</p>
								)}
								{modifiedAt && (
									<p>
										{formatDate(
											modifiedAt,
											profileData?.companyDateFormat ??
												'DD/MM/YYYY'
										)}
									</p>
								)}
							</div>
						);
					}}
				/>
			</Table>
			<div style={{ marginTop: 16, textAlign: 'right' }}>
				<Button
					type="primary"
					onClick={handleSaveClick}
					size="large"
					// style={{
					// 	backgroundColor: '#722ed1',
					// 	borderColor: '#722ed1',
					// }}
				>
					Confirm Upload{' '}
				</Button>
			</div>
			<Modal
				title="Confirm AI-Processed Time Logs"
				open={isModalOpen}
				onCancel={handleCancel}
				maskClosable={false}
				footer={[
					<Button
						key="cancel"
						onClick={handleCancel}
						disabled={isSubmitting}
					>
						Cancel
					</Button>,
					<Button
						key="submit"
						type="primary"
						onClick={handleSubmit}
						loading={isSubmitting}
						disabled={isSubmitting}
					>
						Yes, Continue
					</Button>,
				]}
				width={500}
				closable={false}
			>
				<div>
					<p style={{ marginBottom: 16, fontSize: '14px' }}>
						You have reviewed the AI-generated time log entries.
						<br />
						Please confirm if all details are correct before final
						submission.
					</p>
					<p
						style={{
							color: '#584495',
							fontSize: '13px',
							marginBottom: 0,
						}}
					>
						<strong>Note:</strong> After confirmation, only the
						original supervisor can make changes.
					</p>
				</div>
			</Modal>

			{showConfirmModal && (
				<Modal
					title={
						<div className={styles['confirm-modal']}>
							<ExclamationCircleOutlined
								style={{
									color: 'red',
									alignItems: 'center',
								}}
							/>
						</div>
					}
					className={styles['confirm-modal-container']}
					open={showConfirmModal}
					maskClosable={false}
					closable={false}
					footer={[
						<div
							key={'wrapper'}
							className={styles['confirm-modal__button']}
						>
							<Buttons buttons={myButtons}></Buttons>
						</div>,
					]}
				>
					<p className={styles['confirm-modal-text']}>
						{pendingToggleState ? (
							<p>
								Changing Sunday hours setting will impact
								calculation of hours, <b>overtime hours</b> will
								be set to <b>regular hours</b>.
							</p>
						) : (
							<p>
								Changing Sunday hours setting will impact
								calculation of hours, <b>regular hours</b> will
								be set to <b>overtime hours</b>.
							</p>
						)}
						<p>
							Are you sure you want to proceed with this change?
						</p>
					</p>
				</Modal>
			)}
		</>
	);
};

export default UploadPreview;
